// StopArchetypeClassifier.js
// Component for classifying individual POI stops with manual editing capability

import React, { useState } from 'react';
import { Wand2, Loader, Edit2, Save, X, ChevronDown, ChevronUp, Info } from 'lucide-react';

const ARCHETYPES = [
  { id: 'excited_experientialist', name: 'Excited Experientialist', emoji: '🎉', color: 'bg-pink-500' },
  { id: 'memory_maker', name: 'Memory Maker', emoji: '❤️', color: 'bg-red-500' },
  { id: 'pioneering_pathfinder', name: 'Pioneering Pathfinder', emoji: '🗺️', color: 'bg-green-500' },
  { id: 'travel_tech_fluencer', name: 'Travel Tech-fluencer', emoji: '⚡', color: 'bg-yellow-500' },
  { id: 'cultural_sponge', name: 'Cultural Sponge', emoji: '📚', color: 'bg-blue-500' },
  { id: 'aesthetic_hunter', name: 'Aesthetic Hunter', emoji: '📸', color: 'bg-purple-500' },
  { id: 'adrenaline_junkie', name: 'Adrenaline Junkie', emoji: '🏔️', color: 'bg-orange-500' },
  { id: 'zen_master', name: 'Zen Master', emoji: '🧘', color: 'bg-teal-500' }
];

export const StopArchetypeClassifier = ({ poi, onUpdate }) => {
  const [isClassifying, setIsClassifying] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [editedScores, setEditedScores] = useState({});

  const classification = poi.archetypeClassification;
  const hasClassification = classification && classification.primaryArchetype;

  const handleClassify = async () => {
    setIsClassifying(true);
    setError(null);

    try {
      // Call the classification service for a single stop
      const result = await classifyStop(poi);

      // Update the POI with classification
      const updatedPoi = {
        ...poi,
        archetypeClassification: {
          primaryArchetype: result.primaryArchetype,
          secondaryArchetypes: result.secondaryArchetypes,
          archetypeAlignment: result.archetypeAlignment,
          contentAttributes: result.contentAttributes,
          interestTags: result.interestTags,
          engagementFactors: result.engagementFactors,
          classificationDate: new Date().toISOString(),
          isManuallyEdited: false
        }
      };

      onUpdate(updatedPoi);

      console.log('✅ Stop classified successfully (will save to DB when itinerary is saved)');
    } catch (err) {
      console.error('❌ Classification failed:', err);
      setError(err.message || 'Failed to classify stop. Please try again.');
    } finally {
      setIsClassifying(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    // Initialize with existing scores or all zeros
    const initialScores = classification?.archetypeAlignment || {
      excited_experientialist: 0,
      memory_maker: 0,
      pioneering_pathfinder: 0,
      travel_tech_fluencer: 0,
      cultural_sponge: 0,
      aesthetic_hunter: 0,
      adrenaline_junkie: 0,
      zen_master: 0
    };
    setEditedScores(initialScores);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedScores({});
  };

  const handleSaveManualScores = () => {
    // Find primary archetype (highest score)
    const primaryArchetype = Object.entries(editedScores)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

    // Find secondary archetypes (scores >= 0.6)
    const secondaryArchetypes = Object.entries(editedScores)
      .filter(([id, score]) => id !== primaryArchetype && score >= 0.6)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([id]) => id);

    onUpdate({
      ...poi,
      archetypeClassification: {
        ...classification,
        primaryArchetype,
        secondaryArchetypes,
        archetypeAlignment: editedScores,
        classificationDate: new Date().toISOString(),
        isManuallyEdited: true
      }
    });

    setIsEditing(false);
    setEditedScores({});
  };

  const handleScoreChange = (archetypeId, value) => {
    setEditedScores(prev => ({
      ...prev,
      [archetypeId]: parseFloat(value)
    }));
  };

  const getPrimaryArchetype = () => {
    if (!hasClassification) return null;
    return ARCHETYPES.find(a => a.id === classification.primaryArchetype);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'bg-green-500';
    if (score >= 0.6) return 'bg-blue-500';
    if (score >= 0.4) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const primaryArchetype = getPrimaryArchetype();
  const sortedArchetypes = hasClassification
    ? ARCHETYPES.map(archetype => ({
        ...archetype,
        score: (isEditing ? editedScores : classification.archetypeAlignment)[archetype.id] || 0
      })).sort((a, b) => b.score - a.score)
    : ARCHETYPES.map(a => ({ ...a, score: 0 }));

  return (
    <div className="space-y-3 p-3 bg-gray-50 rounded border border-gray-200 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm">Stop Archetype</h4>
          <Info className="w-3 h-3 text-gray-400" title="AI classification for this specific stop" />
        </div>
        <div className="flex gap-2">
          {!isEditing && (
            <button
              onClick={handleStartEditing}
              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              {hasClassification ? 'Edit' : 'Set Manually'}
            </button>
          )}
          <button
            onClick={handleClassify}
            disabled={isClassifying || isEditing}
            className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-1"
          >
            {isClassifying ? (
              <>
                <Loader className="w-3 h-3 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Wand2 className="w-3 h-3" />
                {hasClassification ? 'Re-classify' : 'Classify with AI'}
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
          {error}
        </div>
      )}

      {/* Editing Mode */}
      {isEditing && (
        <div className="space-y-3 p-3 bg-white rounded border-2 border-blue-300">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">Manual Adjustment</span>
            <div className="flex gap-2">
              <button
                onClick={handleCancelEditing}
                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
              <button
                onClick={handleSaveManualScores}
                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1"
              >
                <Save className="w-3 h-3" />
                Save
              </button>
            </div>
          </div>

          {ARCHETYPES.map(archetype => (
            <div key={archetype.id} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1">
                  <span>{archetype.emoji}</span>
                  <span>{archetype.name}</span>
                </span>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={editedScores[archetype.id]?.toFixed(2) || '0.00'}
                  onChange={(e) => handleScoreChange(archetype.id, e.target.value)}
                  className="w-16 px-1 py-0.5 border rounded text-xs"
                />
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={editedScores[archetype.id] || 0}
                onChange={(e) => handleScoreChange(archetype.id, e.target.value)}
                className="w-full h-1.5 accent-purple-600"
              />
            </div>
          ))}
        </div>
      )}

      {/* Display Mode */}
      {!isEditing && hasClassification && primaryArchetype && (
        <div className="space-y-2">
          {/* Primary Archetype Compact Display */}
          <div className="p-2 bg-white rounded border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">{primaryArchetype.emoji}</span>
                <div>
                  <div className="font-medium text-xs">{primaryArchetype.name}</div>
                  <div className="text-xs text-gray-500">
                    {Math.round(classification.archetypeAlignment[primaryArchetype.id] * 100)}% match
                    {classification.isManuallyEdited && (
                      <span className="ml-1 text-blue-600">(edited)</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-gray-500 hover:text-gray-700"
              >
                {showAll ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {showAll && (
              <div className="space-y-2 mt-3 pt-3 border-t border-gray-200">
                {sortedArchetypes.map(archetype => (
                  <div key={archetype.id} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1">
                        <span>{archetype.emoji}</span>
                        <span>{archetype.name}</span>
                      </span>
                      <span className="font-medium">{Math.round(archetype.score * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${getScoreColor(archetype.score)}`}
                        style={{ width: `${archetype.score * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!hasClassification && !isClassifying && !isEditing && (
        <div className="text-center py-3 text-gray-500 text-xs">
          <p>Not classified yet. Click "Classify" to analyze this stop.</p>
        </div>
      )}
    </div>
  );
};

// Classification service function for individual stops
async function classifyStop(poi) {
  // Extract stop content for classification
  const stopContent = {
    name: poi.name,
    description: poi.description,
    inDepthDescription: poi.inDepthDescription,
    category: poi.category,
    historicalContext: poi.historicalContext,
    culturalSignificance: poi.culturalSignificance,
    tips: poi.tips,
    keywords: poi.keywords
  };

  // Call Backend Cloud Function
  const functionUrl = process.env.REACT_APP_CLASSIFY_FUNCTION_URL ||
                      'https://backend-classifyguidearchetype-ybvgkh7xrq-uc.a.run.app';

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stopContent  // Send as stopContent instead of guideContent
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Classification failed: ${response.statusText}`);
  }

  const result = await response.json();
  return result;
}

export default StopArchetypeClassifier;
